import { greys, mainColors } from '../styling/theme';
import { Status } from '../types/redux/data/dataTypes';
import { KidDocumentStatus } from '../components/pages/raidr/kidsV2/types/KidEditorV2.types';

const isValidHex = (hex: string) => /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);

const getChunksFromString = (st: string, chunkSize: number) =>
  st.match(new RegExp(`.{${chunkSize}}`, 'g'));

const convertHexUnitTo256 = (hexStr: string) =>
  parseInt(hexStr.repeat(2 / hexStr.length), 16);

export const hexToRGBA = (hex: string, alpha: number) => {
  if (!isValidHex(hex)) {
    throw new Error(`Invalid HEX: ${hex}`);
  }
  const chunkSize = Math.floor((hex.length - 1) / 3);
  const hexArr = getChunksFromString(hex.slice(1), chunkSize);
  const [r, g, b] = hexArr!.map(convertHexUnitTo256);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const mapStatusToColor = (status: Status) => {
  switch (status) {
    case Status.Alert:
    case Status.PassAlert:
      return mainColors.Alert;
    case Status.Fail:
    case Status.removedPosition:
      return mainColors.Fail;
    case Status.Pass:
      return mainColors.Pass;
    case Status.NA:
      return mainColors.NA;
    case Status.alteredPosition:
      return mainColors.Pass;
    case Status.noChange:
      return mainColors.NA;
    case Status.newPosition:
      return mainColors.fourthPurple;
    case Status.multiplePositions:
      return mainColors.mainGold;
    default:
      return mainColors.NA;
  }
};

export type RequestStatusType = 'success' | 'fail' | 'loading' | 'idle';

export const getAuthMessageColor = (authStatus: RequestStatusType): string => {
  switch (authStatus) {
    case 'success':
      return mainColors.Pass;
    case 'fail':
      return mainColors.Fail;
    default:
      return greys.grey600;
  }
};

export const COMPLIANCE_COLOUR_MAP: Record<string, string> = {
  UCITS: 'bg-red-200 outline-red-400',
  Prospectus: 'bg-orange-200 outline-orange-400',
  'Supplementary Rules': 'bg-cyan-200 outline-cyan-400',
  Commitment: 'bg-green-200 outline-green-400',
  'Value at Risk': 'bg-blue-200 outline-blue-400',
  Leverage: 'bg-purple-200 outline-purple-400',
  NURS: 'bg-teal-200 outline-teal-400',
  RIAIF: 'bg-yellow-200 outline-yellow-400',
  'CSSF Risk Spreading': 'bg-pink-200 outline-pink-400',
  AIFMD: 'bg-violet-200 outline-violet-400',
  Market: 'bg-yellow-300 outline-yellow-500',
  Counterparty: 'bg-red-200 outline-red-400',
  Credit: 'bg-orange-200 outline-orange-400',
  Liquidity: 'bg-green-200 outline-green-400',
  AIF: 'bg-blue-200 outline-blue-400',
  ACT40: 'bg-purple-200 outline-purple-400',
};

export const getComplianceSubcategoryColor = (subcategory: string): string => {
  return COMPLIANCE_COLOUR_MAP[subcategory] || 'bg-gray-200 outline-gray-400';
};

// ---------------------------------------------------------------------------
// KID document status colours
// ---------------------------------------------------------------------------
export const KID_STATUS_COLOR: Record<KidDocumentStatus, string> = {
  [KidDocumentStatus.Editing]:      '#1d4ed8', // blue-700
  [KidDocumentStatus.Edited]:       '#7e22ce', // purple-700
  [KidDocumentStatus.Rejected]:     '#b91c1c', // red-700
  [KidDocumentStatus.Reviewed]:     '#15803d', // green-700
  [KidDocumentStatus.Published]:    '#15803d', // green-700
  [KidDocumentStatus.FiguresAlert]: '#f59e0b', // amber-500
  [KidDocumentStatus.NoDocument]:   '#6b7280', // gray-500
  [KidDocumentStatus.Deactivated]:  '#9ca3af', // gray-400
};

export const getRiskColourClass = (
  rate: number,
  thresholds: number[] = [10, 5, 0],
  colourClasses: string[] = [
    'text-red-600',
    'text-yellow-600',
    'text-green-600',
  ],
): string => {
  if (rate === 0) {
    return colourClasses[colourClasses.length - 1] || 'text-green-600';
  }

  for (let i = 0; i < thresholds.length; i++) {
    if (rate > thresholds[i]) {
      return colourClasses[i] || 'text-green-600';
    }
  }

  // Fallback if none matched
  return colourClasses[colourClasses.length - 1] || 'text-green-600';
};
